import type { SerialEntry } from "@/widgets/common/serialList/types";

/**
 * Pure helpers for the serial filter modal (thunks = workflow, logic.ts =
 * calculation — section 43).
 */

/** Case-insensitive search across the fields a user would scan by eye. */
export function filterSerialsByQuery(
  serials: SerialEntry[],
  query: string,
): SerialEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return serials;
  }
  return serials.filter(
    (entry) =>
      entry.serial.toLowerCase().includes(q) ||
      entry.mpKey.toLowerCase().includes(q) ||
      entry.id.toLowerCase().includes(q),
  );
}

/** Serial prefix before `#` — the machine line: `HD#…` → `HD`. */
export function serialMachineGroup(entry: SerialEntry): string {
  const hash = entry.serial.indexOf("#");
  return hash > 0 ? entry.serial.slice(0, hash) : "OTHER";
}

/** One machine-line block for the modal (group heading + its serials). */
export interface SerialGroup {
  group: string;
  serials: SerialEntry[];
}

/**
 * Groups serials by machine line (HD / HDX / NX, …) preserving both the
 * first-seen group order and each group's list order — the modal renders
 * one check list per printer line.
 */
export function groupSerialsByMachine(entries: SerialEntry[]): SerialGroup[] {
  const groups = new Map<string, SerialEntry[]>();
  for (const entry of entries) {
    const group = serialMachineGroup(entry);
    const bucket = groups.get(group);
    if (bucket) {
      bucket.push(entry);
    } else {
      groups.set(group, [entry]);
    }
  }
  return [...groups].map(([group, serials]) => ({ group, serials }));
}
